import { value } from '@takeshape/util';
import compose from 'lodash/fp/compose.js';
import unset from 'lodash/fp/unset.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'Rise.ai Gifting';
const serviceProvider = {
    name: 'riseAiGifting',
    title,
    description: 'Rise.ai gift card & store credit solutions',
    categories: ['gift card', 'store credit'],
    links: [{ title: 'Homepage', url: 'https://rise.ai/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('rise.ai'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'rise-ai-gifting'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: title
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'RiseaiGifting'
            },
            shopUrl: {
                title: 'Shop URL',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                type: 'object',
                properties: {
                    token: {
                        title: 'Access Token',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        },
        required: ['title', 'id', 'namespace', 'shopUrl', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'shopUrl', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            shopUrl: {
                widget: 'singleLineText',
                instructions: 'Your shopify domain such as amazingshop.myshopify.com'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'Rise.ai Gifting API access token'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? title,
            provider: 'riseAiGifting',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            options: {
                endpoint: 'https://application.rise-ai.com/integrations/inbound/gifting/',
                spec: 'https://raw.githubusercontent.com/takeshape/openapi-specs/7345c8bf3b192abc369424603bab3d496862ca5a/specs/rise.ai-gifting/v1.yml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                shopUrl: formData.shopUrl,
                searchParams: {
                    shop_url: formData.shopUrl
                },
                headers: {
                    'content-type': 'application/json'
                }
            }
        };
        const updated = updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'x-rise-gifting-api-key',
            prefix: ''
        });
        return updated;
    },
    specTransform: (spec) => compose(unset(['components', 'parameters', 'shop-url']), unset(['paths', '/session/{session_id}', 'get', 'parameters', 0]), unset(['paths', '/session/{session_id}', 'post', 'parameters', 0]), unset(['paths', '/session/{session_id}/completed', 'post', 'parameters', 0]), unset(['paths', '/session/{session_id}/gift/{gift_id}', 'put', 'parameters', 0]), unset(['paths', '/session/{session_id}/gift/{gift_id}', 'delete', 'parameters', 0]), unset(['paths', '/assets', 'get', 'parameters']))(spec),
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/assets')
    })
};
export default serviceProvider;
